import { useEffect, useMemo, useState } from 'react';
import DOMPurify from 'dompurify';
import { RefreshCw, FileText, AlertTriangle } from 'lucide-react';
import { documentsApi } from '../../services/api';
import { withSignaturePlaceholder } from '../../utils/contractSignature';
import { FILLED_FIELD_CLASSES } from '../../utils/contractFields';
import { textTemplateEntity, textTemplateRecord } from '../../utils/textTemplateRecord';
import '../../styles/contractDoc.css';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const isBlank = (v) => v == null || String(v).trim() === '';

const sourceLabels = (record) => ({ bullhorn: record.fieldSource, manual: 'You enter this', date: 'Automatic date' });

/**
 * The "Customize" step for a text template. There is no PDF to place fields on:
 * the text is filled in from the Bullhorn record it is written for (the
 * placement, candidate or contact) when there is one, and anything Bullhorn
 * doesn't have (plus the fields the sender types in) has to be completed here
 * before the document can be sent. A template that only uses typed-in and
 * automatic fields needs no record at all.
 *
 * Values typed here are written into the page's own `fieldValues` (keyed by the
 * document-scoped field id), so they travel in the normal `prefilled_values` of
 * the send and override whatever Bullhorn returned.
 *
 * `onStatus({ loading, ready, blank })` tells the page whether it may proceed.
 */
export default function TextTemplateFillIn({
  template,
  documentFields,
  fieldValues,
  setFieldValues,
  placement,
  candidate,
  company,
  onStatus,
}) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const entityType = textTemplateEntity(template.entity_type);
  const record = textTemplateRecord(entityType);
  const SOURCE_LABEL = sourceLabels(record);

  // Document-scoped field id for a template field id (the wizard renames them).
  const docIdFor = (fieldId) =>
    documentFields.find((f) => String(f.original_id ?? f.id) === String(fieldId))?.id ?? fieldId;

  // What the sender has entered so far, keyed by the template's own field id.
  const overrides = useMemo(() => {
    const out = {};
    for (const f of documentFields) {
      const v = fieldValues[f.id];
      if (!isBlank(v)) out[String(f.original_id ?? f.id)] = v;
    }
    return out;
  }, [documentFields, fieldValues]);
  const overridesKey = JSON.stringify(overrides);

  // Who the preview is for: a placement template previews the first placement's
  // candidate if there is one; otherwise it is the recipient entered in the
  // previous step, as the kind of record the template is written for.
  const recipient = entityType === 'placement' && placement
    ? {
        bullhorn_id: placement.candidate_id ?? null,
        name: placement.candidate_name || placement.name,
        email: placement.candidate_email || '',
        entity_type: 'placement',
        placement_recipient_type: 'candidate',
        placement_id: placement.id,
        company_id: placement.company_id ?? null,
      }
    : candidate?.email
      ? {
          bullhorn_id: candidate.bullhorn_id || null,
          name: candidate.name || candidate.email,
          email: candidate.email,
          entity_type: entityType,
          // A contact's linked company fills the "Client Company" fields.
          ...(entityType === 'contact' && company?.bullhorn_id ? { company_id: company.bullhorn_id } : {}),
        }
      : null;
  const recipientKey = recipient ? `${recipient.placement_id || ''}|${recipient.company_id || ''}|${recipient.email}` : '';

  // Re-render the contract (debounced) as the sender fills things in.
  useEffect(() => {
    if (!recipient) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await documentsApi.testPreview({
          template_id: template.id,
          recipient,
          prefilled_values: overrides,
        });
        if (!cancelled) {
          setPreview(data);
          setError('');
        }
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || 'Could not prepare the document.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 500);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template.id, recipientKey, overridesKey]);

  const fields = (preview?.fields || []).filter((f) => !f.auto_pending);
  const valueOf = (f) => {
    const typed = fieldValues[docIdFor(f.field_id)];
    return !isBlank(typed) ? typed : f.value;
  };
  const blank = fields.filter((f) => isBlank(valueOf(f)));

  useEffect(() => {
    onStatus?.({
      loading,
      ready: !!recipient && !loading && !error && !!preview && blank.length === 0,
      blank: blank.map((f) => f.label),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, error, !!preview, !!recipient, blank.map((f) => f.field_id).join('|')]);

  if (!recipient) {
    return (
      <div className="px-4 sm:px-6 lg:px-8" data-testid="text-template-no-recipient">
        <div className="neumorphic rounded-lg p-6 text-sm text-muted-foreground flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-600" />
          Enter who this is going to first, then come back to fill it in.
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in px-4 sm:px-6 lg:px-8" data-testid="step-text-template">
      <div className="mb-4">
        <h2 className="font-heading text-xl font-semibold">Fill in {template.name}</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Everything below must have a value before this can be sent.
          {entityType !== 'placement' ? (
            <>
              {' '}Previewing <span className="font-medium text-foreground">{candidate?.name || candidate?.email}</span>
              {' '}— fields are filled in from their {record.label.toLowerCase()} record in Bullhorn when sent.
            </>
          ) : placement ? (
            <>
              {' '}Previewing <span className="font-medium text-foreground">{placement.candidate_name || placement.name}</span>’s placement
              — each placement is filled in from its own Bullhorn record when sent.
            </>
          ) : (
            <> No placement is selected, so fields that come from a placement have to be typed in.</>
          )}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-4 items-start">
        <div className="neumorphic rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2 text-xs text-muted-foreground">
            <FileText className="w-3.5 h-3.5" />
            The document as the {entityType === 'contact' ? 'contact' : 'candidate'} will see it
            {loading && <RefreshCw className="w-3 h-3 animate-spin ml-1" />}
          </div>
          {error ? (
            <p className="text-sm text-red-600">{error}</p>
          ) : (
            <div
              className={`bg-white border rounded-md p-6 min-h-[300px] max-h-[60vh] overflow-y-auto contract-doc ${FILLED_FIELD_CLASSES}`}
              data-testid="text-template-preview"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(withSignaturePlaceholder(preview?.preview_html || '', entityType === 'contact' ? 'contact' : 'candidate')) }}
            />
          )}
        </div>

        <div className="neumorphic rounded-lg p-4 space-y-3" data-testid="text-template-fields">
          <h3 className="text-xs font-semibold uppercase text-muted-foreground">Fields</h3>
          {fields.length === 0 && !loading && <p className="text-xs text-muted-foreground">No fields in this document.</p>}
          {fields.map((f) => {
            const empty = isBlank(valueOf(f));
            const typed = f.source === 'manual' && f.field_type === 'date' ? 'date' : f.field_type === 'number' && f.source === 'manual' ? 'number' : 'text';
            return (
              <div key={f.field_id} className="space-y-1">
                <Label className="text-xs">
                  {f.label}
                  <span className="ml-2 text-[10px] text-muted-foreground font-normal">{SOURCE_LABEL[f.source] || ''}</span>
                  {empty && <span className="ml-2 text-[10px] text-red-600 font-normal">required</span>}
                </Label>
                <Input
                  type={typed}
                  value={valueOf(f) ?? ''}
                  onChange={(e) => setFieldValues((prev) => ({ ...prev, [docIdFor(f.field_id)]: e.target.value }))}
                  className={empty ? 'border-red-400' : ''}
                  data-testid={`text-template-field-${f.field_id}`}
                />
                {empty && f.source === 'bullhorn' && (
                  <p className="text-[11px] text-muted-foreground">Bullhorn has no value for this. Type one in to continue.</p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
